import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Icône marker (assets CDN pour éviter les soucis de bundling)
const exactIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41],
});

const NOMINATIM = 'https://nominatim.openstreetmap.org/search';

/**
 * Carte Leaflet/OSM pour la fiche bien.
 * RGPD : en mode public on ne montre QUE le secteur approximatif (cercle flou ~800 m),
 * jamais un pin précis. Le marker exact n'apparaît qu'en interne (isConfidential + coords exactes).
 */
const LocationMap = ({ ville, secteur, exactLat, exactLon, approxLat, approxLon, isConfidential = false }) => {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const [resolved, setResolved] = useState(null); // {lat, lon} | null
  const [failed, setFailed] = useState(false);

  const cleanVille = ville && ville !== '—' ? ville : '';

  // Décompose la localisation saisie en { city, sector }.
  // Secteurs fréquents : "Nice – Centre-ville", "nice port", "centre ville", "Nice".
  const parseLocation = () => {
    let city = cleanVille.trim();
    let sector = secteur && secteur !== '—' ? secteur.trim() : '';
    const dashSplit = (s) => s.split(/\s+[–—-]\s+/).map(t => t.trim()).filter(Boolean);
    if (!city && sector) {
      const parts = dashSplit(sector);
      if (parts.length >= 2) {
        city = parts[0]; sector = parts.slice(1).join(' ');
      } else {
        // Pas de séparateur : on suppose que le 1er mot est la ville (ex: "nice port")
        const words = sector.split(/\s+/);
        if (words.length >= 2) { city = words[0]; }
      }
    } else if (city && sector) {
      const parts = dashSplit(sector);
      if (parts.length >= 2 && parts[0].toLowerCase() === city.toLowerCase()) {
        sector = parts.slice(1).join(' ');
      }
    }
    return { city, sector };
  };

  // 1) Détermine les coordonnées
  useEffect(() => {
    let cancelled = false;
    const useExact = isConfidential && exactLat != null && exactLon != null;
    if (useExact) {
      setResolved({ lat: Number(exactLat), lon: Number(exactLon), exact: true });
      return;
    }
    if (approxLat != null && approxLon != null) {
      setResolved({ lat: Number(approxLat), lon: Number(approxLon), exact: false });
      return;
    }

    const getJson = async (params) => {
      const r = await fetch(`${NOMINATIM}?${new URLSearchParams({ format: 'json', limit: '1', countrycodes: 'fr', ...params })}`, {
        headers: { 'Accept': 'application/json' },
      });
      return r.json();
    };

    // Géocodage robuste en 2 temps (zone approximative, restreint à la France) :
    //  1. Résolution structurée de la VILLE (fiable) → centre + bounding box.
    //  2. Recherche du quartier BORNÉE à la ville (bounded=1) pour ne jamais sortir de la commune.
    //     Évite les faux positifs lointains (ex: "nice port" → port de Cap d'Ail).
    (async () => {
      try {
        const { city, sector } = parseLocation();
        if (!city && !sector) { if (!cancelled) setFailed(true); return; }

        let center = null;
        let bbox = null; // [south, north, west, east]
        if (city) {
          const c = await getJson({ city });
          if (cancelled) return;
          if (Array.isArray(c) && c.length > 0) {
            center = { lat: parseFloat(c[0].lat), lon: parseFloat(c[0].lon) };
            bbox = c[0].boundingbox;
          }
        }

        // Affinage quartier borné à la ville
        if (bbox && (sector || (secteur && secteur !== '—'))) {
          const vb = `${bbox[2]},${bbox[0]},${bbox[3]},${bbox[1]}`; // west,south,east,north
          const q = (secteur && secteur !== '—' ? secteur : sector).trim();
          const d = await getJson({ q, viewbox: vb, bounded: '1' });
          if (cancelled) return;
          if (Array.isArray(d) && d.length > 0) {
            setResolved({ lat: parseFloat(d[0].lat), lon: parseFloat(d[0].lon), exact: false });
            return;
          }
        }

        if (center) { setResolved({ ...center, exact: false }); return; }

        // Fallback : recherche libre "quartier, ville, France"
        const fq = [sector, city, 'France'].filter(Boolean).join(', ');
        const f = await getJson({ q: fq });
        if (cancelled) return;
        if (Array.isArray(f) && f.length > 0) {
          setResolved({ lat: parseFloat(f[0].lat), lon: parseFloat(f[0].lon), exact: false });
        } else {
          setFailed(true);
        }
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [ville, secteur, exactLat, exactLon, approxLat, approxLon, isConfidential]);

  // 2) Rendu de la carte Leaflet
  useEffect(() => {
    if (!resolved || !mapRef.current) return;
    if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; }
    const zoom = resolved.exact ? 16 : 13;
    const map = L.map(mapRef.current, { scrollWheelZoom: false, attributionControl: true }).setView([resolved.lat, resolved.lon], zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap',
    }).addTo(map);
    if (resolved.exact) {
      L.marker([resolved.lat, resolved.lon], { icon: exactIcon }).addTo(map);
    } else {
      // Zone approximative : cercle flou de ~800 m (aucun pin précis)
      L.circle([resolved.lat, resolved.lon], {
        radius: 800, color: '#e11d48', weight: 1.5, fillColor: '#fb7185', fillOpacity: 0.22,
      }).addTo(map);
    }
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } };
  }, [resolved]);

  if (failed && !resolved) {
    return (
      <div className="aspect-[4/3] rounded-lg bg-slate-100 flex items-center justify-center" data-testid="view-map-fallback">
        <div className="text-center">
          <i className="bi bi-geo-alt text-3xl text-slate-300" />
          <p className="text-xs text-slate-500 mt-2 font-semibold">{cleanVille || 'Secteur'}{secteur ? ` — ${secteur}` : ''}</p>
        </div>
      </div>
    );
  }

  return <div ref={mapRef} className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 z-0" data-testid="view-map-leaflet" />;
};

export default LocationMap;
